import { Ionicons } from '@expo/vector-icons';
import React, { type ReactNode } from 'react';
import { Modal, Pressable, ScrollView, StyleSheet, View, useWindowDimensions } from 'react-native';
import { KeyboardAvoidingView } from 'react-native-keyboard-controller';
import { useSafeAreaInsets } from 'react-native-safe-area-context';

import { ThemedText } from '@/components/themed-text';
import { cn } from '@/utils/cn';

/**
 * Casca dos sheets de equipe: sobe do rodapé no celular e vira um cartão no
 * meio da tela no desktop, com a altura do conteúdo (o `Sheet` de `ui/` fixa
 * 90%, alto demais para um assistente de três perguntas).
 *
 * O toast do app vive na raiz, atrás deste Modal: erro aqui é inline.
 */
export function TeamSheet({
  open,
  onClose,
  title,
  subtitle,
  busy,
  footer,
  children,
}: {
  open: boolean;
  onClose: () => void;
  title: string;
  /** "1 de 3", ou uma linha curta. */
  subtitle?: string;
  /** Enquanto grava, fechar fica travado. */
  busy?: boolean;
  footer?: ReactNode;
  children: ReactNode;
}) {
  const { width, height } = useWindowDimensions();
  const insets = useSafeAreaInsets();
  const isWide = width >= 768;
  const close = busy ? undefined : onClose;

  return (
    <Modal
      visible={open}
      transparent
      animationType={isWide ? 'fade' : 'slide'}
      onRequestClose={close}
    >
      <View
        className={cn(
          'flex-1 bg-black/40',
          isWide ? 'items-center justify-center p-6' : 'justify-end',
        )}
      >
        <Pressable style={StyleSheet.absoluteFill} onPress={close} accessibilityLabel="Fechar" />
        <KeyboardAvoidingView
          behavior="padding"
          style={{ width: '100%', maxWidth: isWide ? 460 : undefined }}
        >
          <View
            className={cn('bg-card pt-5', isWide ? 'rounded-3xl' : 'rounded-t-3xl')}
            style={{
              borderCurve: 'continuous',
              maxHeight: height * 0.9,
              paddingBottom: isWide ? 20 : Math.max(insets.bottom, 12) + 8,
            }}
          >
            <View className="mb-3 flex-row items-start gap-3 px-5">
              <View className="flex-1 gap-0.5">
                <ThemedText className="font-bold text-lg leading-6 text-neutral-900">
                  {title}
                </ThemedText>
                {subtitle ? (
                  <ThemedText className="text-sm text-neutral-500">{subtitle}</ThemedText>
                ) : null}
              </View>
              <Pressable
                onPress={close}
                disabled={busy}
                accessibilityRole="button"
                accessibilityLabel="Fechar"
                className="h-9 w-9 items-center justify-center rounded-full bg-neutral-100 active:bg-neutral-200"
                hitSlop={6}
              >
                <Ionicons className="text-neutral-600" name="close" size={18} />
              </Pressable>
            </View>

            <ScrollView
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
              contentContainerStyle={{ paddingHorizontal: 20, paddingBottom: 4 }}
            >
              {children}
            </ScrollView>

            {footer ? <View className="px-5 pt-4">{footer}</View> : null}
          </View>
        </KeyboardAvoidingView>
      </View>
    </Modal>
  );
}

/** Rodapé padrão: voltar à esquerda (quando há), avançar à direita, mesma altura. */
export function SheetStepFooter({
  onBack,
  backLabel = 'Voltar',
  onNext,
  nextLabel,
  busy,
  error,
}: {
  onBack?: () => void;
  backLabel?: string;
  onNext: () => void;
  nextLabel: string;
  busy?: boolean;
  error?: string | null;
}) {
  return (
    <View className="gap-3">
      {error ? (
        <ThemedText className="text-center text-sm text-error-600">{error}</ThemedText>
      ) : null}
      <View className="flex-row gap-3">
        {onBack ? (
          <Pressable
            onPress={onBack}
            disabled={busy}
            accessibilityRole="button"
            className="h-11 flex-1 items-center justify-center rounded-full border border-neutral-200 bg-card active:bg-neutral-50"
          >
            <ThemedText className="font-semibold text-sm text-neutral-800">{backLabel}</ThemedText>
          </Pressable>
        ) : null}
        <Pressable
          onPress={onNext}
          disabled={busy}
          accessibilityRole="button"
          className={cn(
            'h-11 flex-1 items-center justify-center rounded-full bg-primary active:opacity-90',
            busy ? 'opacity-60' : '',
          )}
        >
          <ThemedText className="font-bold text-sm text-white">
            {busy ? 'Salvando…' : nextLabel}
          </ThemedText>
        </Pressable>
      </View>
    </View>
  );
}
